import pluralize from "pluralize";
import {FiAlertTriangle} from "react-icons/fi";

import {Alert} from "@/components/alert/Alert.tsx";
import {CodeBlock} from "@/components/codeBlock/CodeBlock.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {AnalyzerCreateConfirmDialogProps} from "@/pages/analyzer/Analyzer.types.ts";
import {useAnalyzerContext} from "@/pages/analyzer/hooks/AnalyzerContext.tsx";
import {RUM_SNIPPET_PLACEMENT_HINT} from "@/pages/checks/constants.ts";
import {buildRumSnippet} from "@/pages/checks/utils.ts";

import styles from "./AnalyzerCreateConfirmDialog.module.scss";

export const AnalyzerCreateConfirmDialog = ({
  show,
  actionLabel,
  isGrowOnly,
  rumCount,
  tags,
  loading,
  snippets,
  hasFailures,
  onConfirm,
  onCancel,
  onGoToChecks,
}: AnalyzerCreateConfirmDialogProps) => {
  const {formData} = useAnalyzerContext();
  const isSnippetView = snippets.length > 0;
  const newTagCount = tags.filter(({id}) => !id).length;
  const tagDescription = newTagCount ? `new ${pluralize("Tag", newTagCount)}` : pluralize("Tag", tags.length);
  const tagClause = tags.length ? `, filtered to their ${tagDescription}` : "";

  const template = formData?.snippets.rum2_snippet ?? "";
  const nextStep = hasFailures
    ? "Failed rows show the reason in the Status column."
    : `Your Checks list${tagClause} is next.`;
  const snippetConfirmBtnText = hasFailures ? "Back to results" : "Go to Checks";
  const onSnippetConfirm = hasFailures ? onCancel : onGoToChecks;

  const createConfirmBtnText = isGrowOnly ? "Update Checks" : "Create Checks";
  const effect = isGrowOnly ? "adds the selected Checks to their Group" : "creates the Checks in your account";
  const destination = `This ${effect} and opens your Checks list${tagClause}.`;
  const rumClause = `You get the RUM ${pluralize("snippet", rumCount)} for ${pluralize("Check", rumCount, true)} first.`;
  const summary = rumCount > 0 ? `${destination} ${rumClause}` : destination;

  return (
    <ActionDialog
      show={show}
      title={isSnippetView ? "Install your RUM snippets" : `${actionLabel}?`}
      confirmBtnText={isSnippetView ? snippetConfirmBtnText : createConfirmBtnText}
      loading={isSnippetView ? undefined : loading}
      hasCancelButton={!isSnippetView}
      onConfirm={isSnippetView ? onSnippetConfirm : onConfirm}
      onCancel={onCancel}
      scrollable={isSnippetView}
      showBorders
    >
      <div className={styles.body}>
        {isSnippetView ? (
          <>
            <div>
              A RUM Check reports nothing until its snippet is on the site. {RUM_SNIPPET_PLACEMENT_HINT} {nextStep}
            </div>
            {snippets.map(({name, host, uuid}) => (
              <div key={uuid} className={styles.snippet}>
                <div className="pulseTextBodyBold">
                  {name} · {host}
                </div>
                <CodeBlock code={buildRumSnippet(template, uuid)} onModal />
              </div>
            ))}
          </>
        ) : (
          <>
            <div>{summary}</div>
            <Alert icon={FiAlertTriangle} variant="maintenance" className="mb-0">
              This session dialogue will not be saved. To analyze your domain again, you will need to start a new
              Assistant session — new session suggestions may vary.
            </Alert>
          </>
        )}
      </div>
    </ActionDialog>
  );
};
